import { Component, Story, Knobs, Accents } from './DocTypes';

const PlaygroundStoryKnobs: Knobs = {
  accent: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(Accents),
  },
  size: {
    default: 'default',
    editor: 'dropdown',
    editorOptions: ['default', 'small', 'large'],
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  hideMenuIndicator: {
    default: false,
    editor: 'checkbox',
  },
  menuPopupPosition: {
    default: '',
    editor: 'dropdown',
    editorOptions: [
      'auto',
      'auto-start',
      'auto-end',
      'bottom',
      'bottom-start',
      'bottom-end',
      'top',
      'top-start',
      'top-end',
      'left',
      'left-start',
      'left-end',
      'right',
      'right-start',
      'right-end',
    ],
  },
  buttonStyle: {
    editor: 'textbox',
    default: '',
  },
  buttonContentStyle: {
    editor: 'textbox',
    default: '',
  },
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundStoryKnobsReact: Knobs = {
  ...PlaygroundStoryKnobs,
  buttonStyle: {
    ...PlaygroundStoryKnobs.buttonStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
  buttonContentStyle: {
    ...PlaygroundStoryKnobs.buttonContentStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
};

const playgroundStory: Story = {
  title: 'Playground',
  description: 'A simple menu button',
  coreComponent_v3: {
    html: `<tag3-menu-button{{...knobs}}>
    <div slot="button-menu-header">Menu</div>
    <div slot="button-menu-content">
      <tag3-menu-item name='approve' icon='Tick' text='Approve' description='Use this option to approve'></tag3-menu-item>
      <tag3-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag3-menu-item>
    </div>
</tag3-menu-button>`,
    knobs: PlaygroundStoryKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3MenuButton{{...knobs}}>
    <span slot="button-menu-header">Menu</span>
    <div slot="button-menu-content">
      <Tag3MenuItem name='approve' icon='Tick' text='Approve' description='Use this option to approve' />
      <Tag3MenuItem name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel' />
    </div>
</Tag3MenuButton>`,
    knobs: PlaygroundStoryKnobsReact,
  },
};

const submenu: Story = {
  title: 'Submenu',
  description: 'A menu button with a submenu',
  coreComponent_v3: {
    html: `
    <tag3-menu-button size='default'>
      <div slot="button-menu-header">Menu</div>
      <div slot="button-menu-content">
        <tag3-menu-item name='approve' icon='Tick' text='Approve' is-submenu>
          <tag3-menu-item name='approveThis' text='This item'></tag3-menu-item>
          <tag3-menu-item name='approveAll' text='All items'></tag3-menu-item>
        </tag3-menu-item>
        <tag3-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag3-menu-item>
      </div>
    </tag3-menu-button>
    `,
  },
  reactWrapper_v3: {
    jsx: `<Tag3MenuButton{{...knobs}}>
    <span slot="button-menu-header">Menu</span>
    <div slot="button-menu-content">
      <Tag3MenuItem name='approve' icon='Tick' text='Approve' description='Use this option to approve' isSubmenu>
        <Tag3MenuItem name='approveThis' text='This item'></Tag3MenuItem>
        <Tag3MenuItem name='approveAll' text='All items'></Tag3MenuItem>
      </Tag3MenuItem>
      <Tag3MenuItem name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></Tag3MenuItem>
    </div>
</Tag3MenuButton>`,
    knobs: PlaygroundStoryKnobsReact,
  },
};

export const TagMenuButtonDocs: Component = {
  type: 'component',
  name: 'menu-button',
  description: 'Renders a menu button',
  mobileFriendly: false,
  preRelease: '',
  popular: true,
  props: [
    {
      name: 'accent',
      description: 'The colour of the button',
      type: 'string',
      default: 'btndefault',
    },
    {
      name: 'size',
      description: 'The vertical size of the button',
      type: 'default | large | small',
      default: 'default',
    },
    {
      name: 'type',
      description: 'The type of the button',
      type: 'string',
      default: 'button',
    },
    {
      name: 'disabled',
      description: 'Whether the button is disabled',
      type: 'boolean',
    },
    {
      name: 'hideMenuIndicator',
      description: 'Whether the button indicator is hiden or not',
      type: 'boolean',
    },
    {
      name: 'menuPopupPosition',
      description: 'Whether the popup is positioned',
      type:
        'auto | bottom | bottom-start | bottom-end | top | top-start | top-end | left | left-start | left-end | right | right-start | right-end',
    },
    {
      name: 'buttonStyle',
      description: 'JSX style object in JSON to apply to the button',
      type: 'string',
    },
    {
      name: 'buttonContentStyle',
      description: 'JSX style object in JSON to apply to the content of the button',
      type: 'string',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  methods: [],
  events: [
    {
      name: 'menuItemClick',
      description: 'Triggers when a menu item clicked',
      parameters: [
        {
          name: 'name',
          description: 'The name attribute of the menu item',
          type: 'string',
        },
        {
          name: 'text',
          description: 'The text attribute of the menu item',
          type: 'string',
        },
      ],
    },
  ],
  stories: [playgroundStory, submenu],
};
